import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import { useLocation } from 'react-router-dom';

import { isPaymentPage } from 'constants/payment-pages';
import { useWtrSelector } from 'redux/hooks';
import { getCookies } from 'redux/modules/cookies/selectors';
import { AnalyticsConfig, googleAnalyticsStore } from './google-analytics-store';

export const AnalyticsContext = createContext<AnalyticsConfig | undefined>(undefined);

export const AnalyticsContextProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const { analytics: hasAnalyticsCookie } = useWtrSelector(getCookies);
  const paymentPage = isPaymentPage(location.pathname);

  const store = useMemo(
    () => googleAnalyticsStore(hasAnalyticsCookie && !paymentPage),
    [hasAnalyticsCookie, paymentPage],
  );

  const externalStore = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);

  const [analyticsSettings, setAnalyticsSettings] = useState<AnalyticsConfig>(store.getSnapshot());

  useEffect(() => {
    if (externalStore.clientId && externalStore.sessionId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAnalyticsSettings(externalStore);
    }
  }, [externalStore]);

  return (
    <AnalyticsContext.Provider value={analyticsSettings}>{children}</AnalyticsContext.Provider>
  );
};

export const useAnalyticsContext = () => {
  const config = useContext(AnalyticsContext);
  if (!config) {
    throw new Error('useAnalyticsContext must be used within an AnalyticsContextProvider');
  }
  return config;
};
